/*
 * Compare function which takes into account numbers within the string
 *
 * @param {a} object/string/number - item 1 to compare
 * @param {b} object/string/number - item 2 to compare
 * @param {propertyName} string - [optional] if items are object, you can define what property to sort by
 * @return {number} - return of the sorting assessment
 * */
export function naturalCompare(a: any, b: any, propertyName?: string) {
  let stringA, stringB;

  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }

  if (typeof a === 'string' && typeof b === 'string') {
    // if the input is a string, we compare the strings direclty
    stringA = a;
    stringB = b;
  } else {
    // if the input is an object we take a property name if available,
    // otherwise we pick name or type
    if (!propertyName && a.name && b.name) {
      propertyName = 'name';
    } else if (!propertyName) {
      propertyName = 'type';
    }

    if (typeof a[propertyName.toString()] === 'number' && typeof b[propertyName.toString()] === 'number') {
      return a[propertyName.toString()] - b[propertyName.toString()];
    }

    stringA = a[propertyName.toString()]?.toString() ?? '';
    stringB = b[propertyName.toString()]?.toString() ?? '';
  }

  return stringA.localeCompare(stringB, undefined, {
    numeric: true,
    sensitivity: 'base',
    ignorePunctuation: true,
  });
}

export const sortNullableNumbers = (a: number | null, b: number | null) => {
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
};

export const sortByProperty =
  <T>(property: keyof T, defaultValue: string = '') =>
  (a: T, b: T) =>
    naturalCompare(a[property] ?? defaultValue, b[property] ?? defaultValue);

export const sortByValue =
  <T>(getValue: (item: T) => string | number) =>
  (a: T, b: T) =>
    naturalCompare(getValue(a), getValue(b));

export const sortByDateWithPriority =
  <T>(getDate: (item: T) => Date, getFlag: (item: T) => boolean) =>
  (a: T, b: T) => {
    const aHasFlag = getFlag(a);
    const bHasFlag = getFlag(b);
    if (aHasFlag !== bHasFlag) {
      return aHasFlag ? 1 : -1;
    }
    return aHasFlag ? 0 : getDate(a).getTime() - getDate(b).getTime();
  };
